import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper,
  Grid,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
  TableSortLabel
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import axios from "axios";
import "./casestudy.scss";
import Header from "../../layout/header/Header";
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Drawer from "@mui/material/Drawer";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import Skeleton from "@mui/material/Skeleton";
import JSZip from "jszip";

import axiosInstance from "../../../httpInterceptor";

const API_BASE_URL = process.env.REACT_APP_CASE_STUDY_SERVICE_BASE_URL

const CaseStudy: React.FC = () => {
  // Toggle button state - initialize with "digital" selected
  const [engineeringType, setEngineeringType] = useState<string | null>("digital");
  
  // Filter states
  const [capabilityArea, setCapabilityArea] = useState<string[]>([]);
  const [capability, setCapability] = useState<string[]>([]);
  const [industry, setIndustry] = useState<string[]>([]);
  const [account, setAccount] = useState<string[]>([]);
  const [geography, setGeography] = useState<string[]>([]);
  const [teamSizeOperator, setTeamSizeOperator] = useState("=");
  const [teamSizeValue, setTeamSizeValue] = useState("");
  
  // API data states
  const [capabilityAreas, setCapabilityAreas] = useState<string[]>([]);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [geographies, setGeographies] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [caseStudies, setCaseStudies] = useState<any[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);

  // Pagination and sorting states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string>('case_study_name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  // New state variables for row selection
  const [selected, setSelected] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Ref for aborting filter requests
  const currentFilterAbort = React.useRef<AbortController | null>(null);

  // Add individual loading states for each dropdown
  const [capabilityAreasLoading, setCapabilityAreasLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [geographiesLoading, setGeographiesLoading] = useState(true);
  const [industriesLoading, setIndustriesLoading] = useState(true);

  // New state to track if filters have been applied
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Add state for sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<any | null>(null);
  const [sidebarTab, setSidebarTab] = useState(0); // NEW: Tab state

  // New states for presentation preview
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pptUrl, setPptUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // New state for full screen
  const [isFullScreen, setIsFullScreen] = useState(false);
  const pptIframeRef = useRef<HTMLIFrameElement>(null);

  // New search states
  const [searchQuery, setSearchQuery] = useState("");
  const [queryResults, setQueryResults] = useState<any[]>([]);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);

  // Add a flag to know if search is active
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Add a flag to track initial load and prevent initial data fetch
  const [initialLoad, setInitialLoad] = useState(true);

  // 1. Update fetchInitialData to skip case studies by default
  const fetchInitialData = async (type = engineeringType, skipCaseStudies = true) => {
    try {
      setLoading(true);

      // Fetch capability areas based on selected engineering type
      await fetchCapabilityAreas(type === "quality" ? "Quality Engineering" : "Digital Engineering");

      // Fetch other data in parallel
      await Promise.all([
        fetchAccounts(),
        fetchGeographies(),
        fetchIndustries()
      ]);

      if (skipCaseStudies) {
        setCaseStudies([]);
        setPage(0);
        setFiltersApplied(false);
        return;
      }

      // Prepare empty/default payload
      const basePayload = {
        capabilityarea: [],
        capabilities: [],
        industry: [],
        account: [],
        casestudyfilename: [],
        endmonth: null,
        endyear: null,
        geography: [],
        startmonth: null,
        startyear: null,
        teamsize: "",
        teamsizeoperator: "",
        username: getUserFromSession()
      };

      // Only fetch for the selected practice_unit_id
      const practice_unit_id = type === "quality" ? 2 : 1;
      const payload = {
        practice_unit_id,
        ...basePayload
      };
      
      console.log("Fetching case studies with payload:", payload);
      
      const resp = await axiosInstance.post(
        `${API_BASE_URL}/casestudy/filters`,
        payload,
        { headers: { "X-API-KEY": "PBTteamkey" } }
      );

      console.log("Case studies response:", resp.data);

      // Process and set only the relevant case studies
      const processApiResponse = (data: any[]) => {
        if (!Array.isArray(data)) return [];
        return data
          .map(item => {
            const key = Object.keys(item)[0];
            if (!key) return null;
            return item[key];
          })
          .filter(Boolean);
      };

      setCaseStudies(processApiResponse(resp.data));
      setPage(0);
      setFiltersApplied(false);

    } catch (error) {
      console.error("Error fetching initial data:", error);
      setError("Failed to load initial data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // 2. Update useEffect to call fetchInitialData with the correct type
  useEffect(() => {
    // On initial load, fetch dropdown data but not case studies
    if (initialLoad) {
      fetchInitialData(engineeringType, true).finally(() => {
        setLoading(false);
        setInitialLoad(false);
      });
    } else {
      // On engineering type change, only fetch dropdown data, not case studies
      fetchCapabilityAreas(engineeringType === "quality" ? "Quality Engineering" : "Digital Engineering");
      fetchAccounts();
      fetchGeographies();
      fetchIndustries();
      setCaseStudies([]); // Clear case studies until filters are applied
      setPage(0);
      setFiltersApplied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineeringType]);

  // Function to fetch capability areas based on engineering type
  const fetchCapabilityAreas = async (search: string) => {
    setCapabilityAreasLoading(true);
    try {
      const response = await axiosInstance.post(`${API_BASE_URL}/casestudy/capabilityarea`, { search },
{
    headers: {
      "X-API-KEY": "PBTteamkey" // or however you store it
    }
  }
);
      console.log("Capability areas raw response:", response.data);
      
      // Improved data extraction with detailed logging
      let areas = [];
      if (Array.isArray(response.data)) {
        areas = response.data;
      } else if (response.data.result) {
        areas = response.data.result;
      } else if (response.data.data) {
        areas = response.data.data;
      } else {
        // Try to extract data from response if it's an object with values
        const possibleData = Object.values(response.data);
        if (possibleData.length > 0 && Array.isArray(possibleData[0])) {
          areas = possibleData[0];
        }
      }
      
      console.log("Processed capability areas:", areas);
      setCapabilityAreas(areas);
    } catch (error) {
      console.error("Error fetching capability areas:", error);
      setError("Failed to load capability areas");
    } finally {
      setCapabilityAreasLoading(false);
    }
  };

  // Function to fetch accounts - apply the same pattern to other fetch functions
  const fetchAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await axiosInstance.get(`${API_BASE_URL}/casestudy/account`,{
    headers: {
      "X-API-KEY": "PBTteamkey" // or however you store it
    }
  });
      console.log("Accounts raw response:", response.data);
      
      // Improved data extraction with detailed logging
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data.result) {
        data = response.data.result;
      } else if (response.data.data) {
        data = response.data.data;
      } else {
        // Try to extract data from response if it's an object with values
        const possibleData = Object.values(response.data);
        if (possibleData.length > 0 && Array.isArray(possibleData[0])) {
          data = possibleData[0];
        }
      }
      
      console.log("Processed accounts:", data);
      setAccounts(data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setError("Failed to load accounts");
    } finally {
      setAccountsLoading(false);
    }
  };

  // Function to fetch geographies
  const fetchGeographies = async () => {
    setGeographiesLoading(true);
    try {
      const response = await axiosInstance.get(`${API_BASE_URL}/casestudy/geography`,{
    headers: {
      "X-API-KEY": "PBTteamkey" // or however you store it
    }
  });
      console.log("Geographies response:", response.data);
      
      // Extract data from the nested structure
      let data = [];
      if (response.data && response.data.geography && Array.isArray(response.data.geography)) {
        data = response.data.geography;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data.result) {
        data = response.data.result;
      } else if (response.data.data) {
        data = response.data.data;
      }
      
      console.log("Processed geographies:", data);
      setGeographies(data);
    } catch (error) {
      console.error("Error fetching geographies:", error);
      setError("Failed to load geographies");
    } finally {
      setGeographiesLoading(false);
    }
  };

  // Function to fetch industries
  const fetchIndustries = async () => {
    setIndustriesLoading(true);
    try {
      const response = await axiosInstance.get(`${API_BASE_URL}/casestudy/industry`,{
    headers: {
      "X-API-KEY": "PBTteamkey"
    }
  });
      console.log("Industries response:", response.data);
      
      let data = [];
      if (response.data && response.data.industry && Array.isArray(response.data.industry)) {
        data = response.data.industry;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data.result) {
        data = response.data.result;
      } else if (response.data.data) {
        data = response.data.data;
      }
      
      console.log("Processed industries:", data);
      setIndustries(data);
    } catch (error) {
      console.error("Error fetching industries:", error);
      setError("Failed to load industries");
    } finally {
      setIndustriesLoading(false);
    }
  };

  // Handle toggle button change and fetch appropriate capability areas
  const handleEngineeringTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newType: string | null,
  ) => {
    if (!newType || newType === engineeringType) return;

    setEngineeringType(newType);

    // Reset all filters and results
    setCapabilityArea([]);
    setCapability([]);
    setIndustry([]);
    setAccount([]);
    setGeography([]);
    setTeamSizeOperator("=");
    setTeamSizeValue("");
    setCaseStudies([]);
    setPage(0);
    setSelected([]);
    setSelectAll(false);
    setFiltersApplied(false);

    // Do NOT call fetchInitialData here; the useEffect on engineeringType
    // already refreshes dropdowns without hitting the filter API.
  };
  // Multi-select handlers
  const handleCapabilityAreaChange = (event: SelectChangeEvent<string[]>) => {
    setCapabilityArea(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value as string[]);
  };

  const handleIndustryChange = (event: SelectChangeEvent<string[]>) => {
    setIndustry(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value as string[]);
  };

  const handleAccountChange = (event: SelectChangeEvent<string[]>) => {
    setAccount(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value as string[]);
  };

  const handleGeographyChange = (event: SelectChangeEvent<string[]>) => {
    const selectedGeographies = typeof event.target.value === 'string' 
      ? event.target.value.split(',') 
      : event.target.value as string[];
    
    console.log("Selected geographies:", selectedGeographies);
    setGeography(selectedGeographies);
  };

  // Add this useEffect to fetch capabilities when capabilityArea changes
  useEffect(() => {
    const fetchCapabilities = async () => {
      // Only make the API call if there are selected capability areas
      if (capabilityArea.length > 0) {
        try {
          // setLoading(true); // REMOVE THIS LINE
          const response = await axiosInstance.post(`${API_BASE_URL}/casestudy/capability`, {
            capabilityAreas: capabilityArea
          },{
    headers: {
      "X-API-KEY": "PBTteamkey" // or however you store it
    }
  });
          console.log("Capabilities raw response:", response.data);
          
          // Extract data from the nested structure with the same pattern used for other API responses
          let data = [];
          if (Array.isArray(response.data)) {
            data = response.data;
          } else if (response.data.result) {
            data = response.data.result;
          } else if (response.data.data) {
            data = response.data.data;
          } else {
            // Try to extract data from response if it's an object with values
            const possibleData = Object.values(response.data);
            if (possibleData.length > 0 && Array.isArray(possibleData[0])) {
              data = possibleData[0];
            }
          }
          
          console.log("Processed capabilities:", data);
          setCapabilities(data);
        } catch (error) {
          console.error("Error fetching capabilities:", error);
          setError("Failed to load capabilities");
        } finally {
          // setLoading(false); // REMOVE THIS LINE
        }
      } else {
        // Reset capabilities when no capability areas are selected
        setCapabilities([]);
        setCapability([]);
      }
    };

    fetchCapabilities();
  }, [capabilityArea]);

  // Add this useEffect after your existing useEffect for capability areas

  // Add this useEffect to fetch accounts when industry changes
  useEffect(() => {
    const fetchAccountsByIndustry = async () => {
      // Only make the API call if there are selected industries
      if (industry.length > 0) {
        try {
          // setLoading(true); // REMOVE THIS LINE
          const response = await axiosInstance.post(`${API_BASE_URL}/casestudy/accounts`, {
            industries: industry
          },{
    headers: {
      "X-API-KEY": "PBTteamkey" // or however you store it
    }
  });
          console.log("Accounts by industry raw response:", response.data);
          
          // Extract data from the nested structure with the same pattern used for other API responses
          let data = [];
          if (Array.isArray(response.data)) {
            data = response.data;
          } else if (response.data.result) {
            data = response.data.result;
          } else if (response.data.data) {
            data = response.data.data;
          } else {
            // Try to extract data from response if it's an object with values
            const possibleData = Object.values(response.data);
            if (possibleData.length > 0 && Array.isArray(possibleData[0])) {
              data = possibleData[0];
            }
          }
          
          console.log("Processed accounts from industry filter:", data);
          setAccounts(data);
          // Reset account selections since the available options have changed
          setAccount([]);
        } catch (error) {
          console.error("Error fetching accounts by industry:", error);
          setError("Failed to load accounts for selected industries");
        } finally {
          // setLoading(false); // REMOVE THIS LINE
        }
      } else {
        // If no industries are selected, fetch all accounts
        fetchAccounts();
      }
    };

    fetchAccountsByIndustry();
  }, [industry]);

  // Add handler for capability selection changes
  const handleCapabilityChange = (event: SelectChangeEvent<string[]>) => {
    setCapability(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value as string[]);
  };

  // Define operators
  const operators = ["<", ">", "=", "<=", ">="];

  // Add this function to extract the actual case study data from the nested structure
  const processApiResponse = (data: any[]) => {
    if (!Array.isArray(data)) return [];
    return data
      .map(item => {
        const key = Object.keys(item)[0];
        if (!key) return null;
        return item[key];
      })
      .filter(Boolean);
  };

  // Add this function near the top of your component
  const getUserFromSession = () => {
    const email = sessionStorage.getItem('PBT_EMAIL');
    console.log("Email from session storage:", email);
    return email || "";
  };

  // Then modify the handleApplyFilters function
  const handleApplyFilters = async () => {
    try {
      setFilterLoading(true);
      setError(null);
      setFiltersApplied(true); // SET THIS WHEN APPLYING FILTERS

      if (currentFilterAbort.current) currentFilterAbort.current.abort();
      currentFilterAbort.current = new AbortController();

      // Get user email from session storage
      const userEmail = getUserFromSession();
      
      // Convert geography strings to numbers with complete mapping
      const geoMapping: Record<
        "US" | "UK" | "South Africa" | "EU" | "Japan" | "Germany" | "UAE" | "ASEAN" | "India" | "Singapore" | "MY" | "Sweden" | "Australia" | "Belgium" | "France" | "Georgia" | "Hong Kong" | "Netherlands",
        number
      > = {
        "US": 1,
        "UK": 2,
        "South Africa": 3,
        "EU": 4,
        "Japan": 5,
        "Germany": 6,
        "UAE": 7,
        "ASEAN": 8,
        "India": 9,
        "Singapore": 10,
        "MY": 11,
        "Sweden": 12,
        "Australia": 13,
        "Belgium": 14,
        "France": 15,
        "Georgia": 16,
        "Hong Kong": 17,
        "Netherlands": 18
      };

      const geographyIds = geography.map(geo => {
        // Use the mapping if available
        if (geo in geoMapping) {
          return geoMapping[geo as keyof typeof geoMapping];
        }
        
        // If it's already a number or can be parsed as one
        if (typeof geo === 'number' || !isNaN(parseInt(geo))) {
          return typeof geo === 'string' ? parseInt(geo) : geo;
        }
        
        // Log if a geography value wasn't mapped
        console.warn(`Geography value not mapped: ${geo}`);
        return null;
      }).filter(id => id !== null); // Remove null values
    
      console.log("Geography selections:", geography);
      console.log("Mapped geography IDs:", geographyIds);
    
      // Construct the payload according to the specified format
      const payload = {
        practice_unit_id: engineeringType === "digital" ? 1 : 2,
        username: userEmail,
        capabilityarea: capabilityArea,
        capabilities: capability,
        industry: industry,
        account: account,
        geography: geographyIds,
        casestudyfilename: [], 
        startmonth: null,
        startyear: null,
        endmonth: null,
        endyear: null,
        teamsize: teamSizeValue,
        teamsizeoperator: teamSizeOperator
      };
      
      console.log("Sending filter payload:", payload);
      
      // Make the API call
      const response = await axiosInstance.post(`${API_BASE_URL}/casestudy/filters`, payload, {
        headers: { "X-API-KEY": "PBTteamkey" },
        signal: currentFilterAbort.current.signal,
      });
      console.log("Filter response:", response.data);
      
      // Process the nested response data
      const processedData = processApiResponse(response.data);
      console.log("Processed case study data:", processedData);
      
      setCaseStudies(processedData);
      // Reset to first page when applying new filters
      setPage(0);
      
    } catch (error) {
      console.error("Error applying filters:", error);
      let errorMessage = "Failed to apply filters. Please try again.";
      
      // Extract detailed error message from response if available
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        error.response !== null &&
        "data" in error.response &&
        error.response.data
      ) {
        if (
          typeof error.response.data === "object" &&
          error.response.data !== null &&
          "error" in error.response.data
        ) {
          errorMessage = (error.response.data as { error?: string }).error as string;
        }
        if (
          typeof error.response.data === "object" &&
          error.response.data !== null &&
          "details" in error.response.data
        ) {
          errorMessage += ": " + JSON.stringify((error.response.data as { details?: any }).details);
        }
        if (
          typeof error.response.data === "object" &&
          error.response.data !== null &&
          "message" in error.response.data &&
          typeof (error.response.data as { message?: unknown }).message === "string"
        ) {
          errorMessage += " - " + (error.response.data as { message?: string }).message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setFilterLoading(false);
    }
  };

  // Add sorting function
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Add pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Function to sort data based on current sort settings
  const sortedData = React.useMemo(() => {
    if (!caseStudies.length) return [];
    
    return [...caseStudies].sort((a, b) => {
      let valueA = a[orderBy];
      let valueB = b[orderBy];
      
      // Handle capability_area which is an array
      if (orderBy === 'capability_area') {
        valueA = Array.isArray(valueA) ? valueA.join(', ') : valueA;
        valueB = Array.isArray(valueB) ? valueB.join(', ') : valueB;
      }
      
      // Handle null/undefined values
      if (valueA === null || valueA === undefined) return order === 'asc' ? -1 : 1;
      if (valueB === null || valueB === undefined) return order === 'asc' ? 1 : -1;
      
      // Convert to string for comparison
      valueA = String(valueA).toLowerCase();
      valueB = String(valueB).toLowerCase();
      
      return order === 'asc' 
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    });
  }, [caseStudies, order, orderBy]);

  // Calculate paginated data
  const paginatedData = React.useMemo(() => {
    return sortedData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedData, page, rowsPerPage]);

  // New handler functions for row selection
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Select all rows on current page (use global indices)
      const newSelected = paginatedData.map((_, index) => page * rowsPerPage + index);
      setSelected(prev => {
        // Merge with existing, avoid duplicates
        const merged = new Set([...prev, ...newSelected]);
        return Array.from(merged);
      });
      setSelectAll(true);
    } else {
      // Deselect only rows on current page
      const deselectSet = new Set(paginatedData.map((_, index) => page * rowsPerPage + index));
      setSelected(prev => prev.filter(idx => !deselectSet.has(idx)));
      setSelectAll(false);
    }
  };

  const handleSelectRow = (event: React.ChangeEvent<HTMLInputElement>, globalIndex: number) => {
    const selectedIndex = selected.indexOf(globalIndex);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, globalIndex];
    } else {
      newSelected = selected.filter(idx => idx !== globalIndex);
    }

    setSelected(newSelected);

    // Update page-level selectAll state
    const allOnPageSelected = paginatedData.every((_, idx) =>
      newSelected.includes(page * rowsPerPage + idx)
    );
    setSelectAll(allOnPageSelected);
  };

  const isSelected = (globalIndex: number) => selected.indexOf(globalIndex) !== -1;

  // Add this helper to check if any filter is selected
  const isAnyFilterSelected = capabilityArea.length > 0 ||
    capability.length > 0 ||
    industry.length > 0 ||
    account.length > 0 ||
    geography.length > 0 ||
    teamSizeValue;

  // Update handler for clicking case study name
  const handleCaseStudyClick = (study: any) => {
    setSelectedCaseStudy(study);
    setSidebarTab(0); // Default to Details tab
    setSidebarOpen(true);
  };

  // New handler for case study preview
  const handleCaseStudyPreview = async () => {
    if (!selectedCaseStudy?.case_study_file_link) {
      setPreviewError("Presentation not available for this case study.");
      return;
    }
    try {
      setPreviewLoading(true);
      setPreviewError(null);
      const payload = { file_path: selectedCaseStudy.case_study_file_link };
      const response = await axiosInstance.post(
        `${API_BASE_URL}/casestudy/ppt`,
        payload,
        { headers: { "X-API-KEY": "PBTteamkey" } }
      );

      // Use Office Web Viewer to avoid browser download prompts
      const blobUrl = response.data.blob_url;
      const viewUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(blobUrl)}`;
      setPptUrl(viewUrl);
    } catch (error) {
      setPreviewError("Failed to load presentation. Please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  // REMOVE the auto-fetch on tab switch to stop automatic downloads
  // useEffect(() => {
  //   if (sidebarTab === 1 && selectedCaseStudy) {
  //     if (!pptUrl || pptUrl !== selectedCaseStudy.case_study_file_link) {
  //       handleCaseStudyPreview();
  //     }
  //   }
  // }, [sidebarTab, selectedCaseStudy]);

  // 2. Add the PPT download handler function inside your component, after the other handler functions:
  const handleDownloadPPT = async () => {
    if (!selectedCaseStudy?.case_study_file_link) {
      setPreviewError("No PPT file available.");
      return;
    }
    try {
      setPreviewLoading(true);
      setPreviewError(null);

      // 1. Get the download URL from backend
      const response = await axiosInstance.post(
        `${API_BASE_URL}/casestudy/downloadppt`,
        { file_path: selectedCaseStudy.case_study_file_link },
        { headers: { "X-API-KEY": "PBTteamkey" } }
      );
      const downloadUrl = response.data.downloadUrl;
      if (!downloadUrl) throw new Error("No download URL returned");

      // 2. Trigger browser download directly
      const link = document.createElement('a');
      link.href = downloadUrl;
      // Optional: set filename
      const fileName = decodeURIComponent(downloadUrl.split("/").pop()?.split("?")[0] || "case_study.pptx");
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

    } catch (error) {
      setPreviewError("Failed to download PPT. Please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Add this function inside your component
  const handleBulkDownloadPPT = async () => {
    if (selected.length === 0) return;
    setPreviewLoading(true);
    setPreviewError(null);

    // Get the selected studies' file links
    const selectedStudies = paginatedData.filter((_, idx) => selected.includes(idx));
    const files = selectedStudies
      .map(study => study.case_study_file_link)
      .filter(Boolean);

    if (files.length === 0) {
      setPreviewError("No PPT files available for selected case studies.");
      setPreviewLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/casestudy/downloadppt`,
        { files, action: "d", file_type: "pptx" },
        {
          headers: { "X-API-KEY": "PBTteamkey" },
          responseType: "blob",
        }
      );

      // If multiple files, assume backend returns a zip
      const isZip = response.headers["content-type"] === "application/zip" ||
        (files.length > 1 && response.data);

      const filename =
        files.length === 1
          ? (files[0].split("/").pop() || "case_study") + ".pptx"
          : "case_studies.zip";

      const blob = new Blob([response.data], {
        type: isZip
          ? "application/zip"
          : "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setPreviewError("Failed to download PPT(s). Please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handler for full screen
  const handleFullScreen = () => {
    const iframe = pptIframeRef.current;
    if (iframe) {
      if (iframe.requestFullscreen) {
        iframe.requestFullscreen();
      } else if ((iframe as any).webkitRequestFullscreen) {
        (iframe as any).webkitRequestFullscreen();
      } else if ((iframe as any).msRequestFullscreen) {
        (iframe as any).msRequestFullscreen();
      }
      setIsFullScreen(true);
    }
  };

  // Optional: Listen for fullscreen exit to update state
  useEffect(() => {
    const handleFullScreenChange = () => {
      if (
        !document.fullscreenElement &&
        !(document as any).webkitFullscreenElement &&
        !(document as any).msFullscreenElement
      ) {
        setIsFullScreen(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('msfullscreenchange', handleFullScreenChange);
    };
  }, []);

  // Add this function to normalize items from any API into the shape the main table uses
  const normalizeCaseStudy = (item: any) => {
    const get = (...keys: string[]) =>
      keys.reduce<any>((val, k) => (val !== undefined && val !== null ? val : item[k]), undefined);

    const capabilityArea =
      get('capability_area', 'capabilityArea', 'capabilityarea') ??
      (Array.isArray(get('capabilities')) ? get('capabilities') : undefined);

    return {
      // main fields used by the table
      case_study_name: get('case_study_name', 'caseStudyName', 'title', 'name') ?? '',
      account_name: get('account_name', 'account', 'customer', 'client') ?? '',
      industry: get('industry', 'sector', 'vertical') ?? '',
      end_date: get('end_date', 'endDate', 'completedOn', 'enddate') ?? null,
      team_size: get('team_size', 'teamSize', 'teamsize') ?? '',
      practice_unit: get('practice_unit', 'practiceUnit', 'practice', 'unit') ?? '',
      capability_area: Array.isArray(capabilityArea)
        ? capabilityArea
        : capabilityArea
        ? [capabilityArea]
        : [],
      // keep original fields too (used elsewhere in the drawer)
      capabilities: get('capabilities') ?? [],
      geography: get('geography') ?? [],
      case_study_file_link: get('case_study_file_link', 'file_link', 'ppt', 'pptLink'),
      start_date: get('start_date', 'startDate'),
      case_study_poc: get('case_study_poc', 'poc', 'owner'),

      // <-- Add this line
      match_type: get('match_type'), // <-- Add this line
    };
  };

  // Search handler (replace handleQuerySearch)
  const handleQuerySearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setQueryLoading(true);
    setQueryError(null);
    setIsSearchActive(true);
    try {
      const payload = {
        query: searchQuery,
        file_filter: { files: [] }
      };
      const response = await axiosInstance.post(
        `${API_BASE_URL}/casestudy/query`,
        payload,
        { headers: { "X-API-KEY": "PBTteamkey" } }
      );

      const raw = Array.isArray(response.data) ? response.data : (response.data.result || []);
      // Flatten the array of { [title]: data } objects to just [data, ...]
      interface QueryApiRawItem {
        [key: string]: any;
      }

      // The normalized case study shape for the table
      interface NormalizedCaseStudy {
        case_study_name: string;
        account_name: string;
        industry: string;
        end_date: string | null;
        team_size: string | number;
        practice_unit: string;
        capability_area: string[];
        capabilities: string[];
        geography: string[];
        case_study_file_link?: string;
        start_date?: string;
        case_study_poc?: string;
        [key: string]: any;
      }

      const flat: any[] = (raw as QueryApiRawItem[]).flatMap((obj: QueryApiRawItem) => {
        if (obj && typeof obj === "object" && Object.keys(obj).length === 1) {
          return Object.values(obj);
        }
        return [];
      });
      const normalized = flat.map(normalizeCaseStudy);

      // show results in the main table only
      setCaseStudies(normalized);
      setQueryResults(normalized); // optional to keep for debugging
      setPage(0);
    } catch (err: any) {
      setQueryError("Failed to fetch query results");
      setQueryResults([]);
      setCaseStudies([]);
    } finally {
      setQueryLoading(false);
    }
  };

  // Clear search and restore previous data (filtered or all)
  const handleClearSearch = () => {
    setSearchQuery("");
    setQueryResults([]);
    setIsSearchActive(false);
    setQueryError(null);

    // Reset all filters
    setCapabilityArea([]);
    setCapability([]);
    setIndustry([]);
    setAccount([]);
    setGeography([]);
    setTeamSizeOperator("=");
    setTeamSizeValue("");
    setFiltersApplied(false);

    // --- FIX: Re-fetch dropdown data so dropdowns are not empty ---
    fetchCapabilityAreas("Digital Engineering");
    fetchAccounts();
    fetchGeographies();
    fetchIndustries();

    // Reload all case studies (initial load)
    fetchInitialData();
  };

  // --- Add this useEffect to fix the PPT preview bug ---
  useEffect(() => {
    // When the sidebar opens or the selected case study changes, reset preview state
    setPptUrl(null);
    setPreviewError(null);
    setPreviewLoading(false);
  }, [selectedCaseStudy, sidebarOpen]);

  // --- Add this useEffect to trigger PPT preview ONLY when Presentation tab is clicked ---
  useEffect(() => {
    if (
      sidebarOpen &&
      sidebarTab === 1 &&
      selectedCaseStudy &&
      selectedCaseStudy.case_study_file_link
    ) {
      handleCaseStudyPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarTab, selectedCaseStudy, sidebarOpen]);

  return (
    <div className="case-study-container">
      <Header />
      <Typography variant="h6" sx={{ mt: 2, ml: 7.5, mb: 2, fontWeight: 600, color: "#012063", textAlign: "left" }}>
      </Typography>

      {error && (
        <Typography color="error" sx={{ textAlign: "center", mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ px: 4 }}>
        {/* Left sidebar with filters */}
        <Grid item xs={12} md={3}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: "8px" }}>
            {/* Toggle Buttons */}
            <Box sx={{ mb: 2 }}>
              <ToggleButtonGroup
                value={engineeringType}
                exclusive
                onChange={handleEngineeringTypeChange}
                aria-label="engineering type"
                fullWidth
              >
                <ToggleButton 
                  value="digital" 
                  aria-label="digital engineering"
                  sx={{ 
                    fontWeight: 600,
                    '&.Mui-selected': { 
                      backgroundColor: '#0d5eff', 
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#0a4cd6',
                      }
                    }
                  }}
                >
                  DIGITAL ENGINEERING
                </ToggleButton>
                <ToggleButton 
                  value="quality" 
                  aria-label="quality engineering"
                  sx={{ 
                    fontWeight: 600,
                    '&.Mui-selected': { 
                      backgroundColor: '#0d5eff', 
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#0a4cd6',
                      }
                    }
                  }}
                >
                  QUALITY ENGINEERING
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {/* Skeleton loader for filters */}
            {loading ? (
              <Box>
                <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={80} sx={{ mb: 2, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={36} sx={{ mb: 2, borderRadius: 1 }} />
              </Box>
            ) : engineeringType && (
              <>
                <Divider sx={{ mb: 2 }} />
                
                {/* Capability Area dropdown */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="capability-area-label">Capability Area</InputLabel>
                  <Select
                    labelId="capability-area-label"
                    id="capability-area"
                    multiple
                    value={capabilityArea}
                    label="Capability Area"
                    onChange={handleCapabilityAreaChange}
                    renderValue={(selected) => (selected as string[]).join(', ')}
                    disabled={capabilityAreasLoading}
                    MenuProps={{
                      disableScrollLock: true,
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: 'auto',
                          minWidth: 200,
                        }
                      }
                    }}
                  >
                    {capabilityAreasLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} /> Loading...
                      </MenuItem>
                    ) : capabilityAreas.length > 0 ? (
                      capabilityAreas.map((area) => (
                        <MenuItem key={area} value={area}>
                          <Checkbox checked={capabilityArea.indexOf(area) > -1} />
                          <ListItemText primary={area} />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No capability areas found</MenuItem>
                    )}
                  </Select>
                </FormControl>
                
                {/* Capability dropdown */}
                <FormControl fullWidth sx={{ mb: 2 }} disabled={capabilityArea.length === 0}>
                  <InputLabel id="capability-label">Capability</InputLabel>
                  <Select
                    labelId="capability-label"
                    id="capability"
                    multiple
                    value={capability}
                    label="Capability"
                    onChange={handleCapabilityChange}
                    renderValue={(selected) => (selected as string[]).join(', ')}
                  >
                    {capabilities.length > 0 ? (
                      capabilities.map((cap) => (
                        <MenuItem key={cap} value={cap}>
                          <Checkbox checked={capability.indexOf(cap) > -1} />
                          <ListItemText primary={cap} />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No capabilities found</MenuItem>
                    )}
                  </Select>
                </FormControl>
                
                {/* Industry dropdown */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="industry-label">Industry</InputLabel>
                  <Select
                    labelId="industry-label"
                    id="industry"
                    multiple
                    value={industry}
                    label="Industry"
                    onChange={handleIndustryChange}
                    renderValue={(selected) => (selected as string[]).join(', ')}
                    disabled={industriesLoading}
                    MenuProps={{
                      disableScrollLock: true,
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: 'auto',
                          minWidth: 200,
                        }
                      }
                    }}
                  >
                    {industriesLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} /> Loading...
                      </MenuItem>
                    ) : industries.length > 0 ? (
                      industries.map((ind) => (
                        <MenuItem key={ind} value={ind}>
                          <Checkbox checked={industry.indexOf(ind) > -1} />
                          <ListItemText primary={ind} />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No industries found</MenuItem>
                    )}
                  </Select>
                </FormControl>
                
                {/* Account dropdown */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="account-label">Account</InputLabel>
                  <Select
                    labelId="account-label"
                    id="account"
                    multiple
                    value={account}
                    label="Account"
                    onChange={handleAccountChange}
                    renderValue={(selected) => (selected as string[]).join(', ')}
                    disabled={accountsLoading}
                    MenuProps={{
                      disableScrollLock: true,
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: 'auto',
                          minWidth: 200,
                        }
                      }
                    }}
                  >
                    {accountsLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} /> Loading...
                      </MenuItem>
                    ) : accounts.length > 0 ? (
                      accounts.map((acc) => (
                        <MenuItem key={acc} value={acc}>
                          <Checkbox checked={account.indexOf(acc) > -1} />
                          <ListItemText primary={acc} />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No accounts found</MenuItem>
                    )}
                  </Select>
                </FormControl>
                
                {/* Advanced Search Filter */}
                <Accordion sx={{ mb: 2, boxShadow: 'none', '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ px: 0, py: 0 }}
                  >
                    <Typography sx={{ color: "#0d5eff", fontWeight: 500 }}>
                      Advanced Search Filter
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0 }}>
                    {/* Geography dropdown */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel id="geography-label">Geography</InputLabel>
                      <Select
                        labelId="geography-label"
                        id="geography"
                        multiple
                        value={geography}
                        label="Geography"
                        onChange={handleGeographyChange}
                        renderValue={(selected) => (selected as string[]).join(', ')}
                        disabled={geographiesLoading}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300
                            }
                          }
                        }}
                      >
                        {geographiesLoading ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} /> Loading...
                          </MenuItem>
                        ) : geographies.length > 0 ? (
                          geographies.map((geo) => (
                            <MenuItem key={geo} value={geo}>
                              <Checkbox checked={geography.indexOf(geo) > -1} />
                              <ListItemText primary={geo} />
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>No geographies found</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    
                    {/* Team Size filter */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                      <FormControl sx={{ minWidth: 140 }}>
    <InputLabel id="team-size-operator-label">Team Size Operator</InputLabel>
    <Select
      labelId="team-size-operator-label"
      id="team-size-operator"
      value={teamSizeOperator}
      label="Team Size Operator"
      onChange={(e) => setTeamSizeOperator(e.target.value)}
      size="small"
    >
      {operators.map((op) => (
        <MenuItem key={op} value={op}>{op}</MenuItem>
      ))}
    </Select>
  </FormControl>
  <TextField 
    size="small"
    type="number" 
    value={teamSizeValue}
    onChange={(e) => setTeamSizeValue(e.target.value)}
    placeholder="Size"
    InputProps={{ 
      inputProps: { min: 0 } 
    }}
  />
                    </Box>
                  </AccordionDetails>
                </Accordion>
                
                {/* Filter summary chips and clear button */}
                <Box sx={{ mt: 2, mb: 2 }}>
                  {(capabilityArea.length > 0 || capability.length > 0 || industry.length > 0 || 
                    account.length > 0 || geography.length > 0 || teamSizeValue) && (
                    <>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Selected Filters:
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {capabilityArea.map((area) => (
                          <Chip
                            key={`ca-${area}`}
                            label={`CA: ${area}`}
                            size="small"
                            onDelete={() => {
                              setCapabilityArea(capabilityArea.filter(a => a !== area));
                            }}
                            sx={{ mb: 1 }}
                          />
                        ))}
                        {capability.map((cap) => (
                          <Chip
                            key={`cap-${cap}`}
                            label={`Cap: ${cap}`}
                            size="small"
                            onDelete={() => {
                              setCapability(capability.filter(c => c !== cap));
                            }}
                            sx={{ mb: 1 }}
                          />
                        ))}
                        {industry.map((ind) => (
                          <Chip
                            key={`ind-${ind}`}
                            label={`Ind: ${ind}`}
                            size="small"
                            onDelete={() => {
                              setIndustry(industry.filter(i => i !== ind));
                            }}
                            sx={{ mb: 1 }}
                          />
                        ))}
                        {account.map((acc) => (
                          <Chip
                            key={`acc-${acc}`}
                            label={`Acc: ${acc}`}
                            size="small"
                            onDelete={() => {
                              setAccount(account.filter(a => a !== acc));
                            }}
                            sx={{ mb: 1 }}
                          />
                        ))}
                        {geography.map((geo) => (
                          <Chip
                            key={`geo-${geo}`}
                            label={`Geo: ${geo}`}
                            size="small"
                            onDelete={() => {
                              setGeography(geography.filter(g => g !== geo));
                            }}
                            sx={{ mb: 1 }}
                          />
                        ))}
                        {teamSizeValue && (
                          <Chip
                            key="team-size"
                            label={`Team Size ${teamSizeOperator} ${teamSizeValue}`}
                            size="small"
                            onDelete={() => {
                              setTeamSizeValue("");
                            }}
                            sx={{ mb: 1 }}
                          />
                        )}
                      </Stack>
                      <Button 
                        variant="text" 
                        size="small" 
                        onClick={() => {
                          setCapabilityArea([]);
                          setCapability([]);
                          setIndustry([]);
                          setAccount([]);
                          setGeography([]);
                          setTeamSizeValue("");
                          setTeamSizeOperator("=");
                        }}
                        sx={{ mt: 1, fontSize: '0.75rem' }}
                      >
                        Clear All Filters
                      </Button>
                    </>
                  )}
                </Box>
                
                <Button 
                  variant="contained" 
                  fullWidth
                  onClick={handleApplyFilters}
                  disabled={filterLoading || !isAnyFilterSelected}
                  sx={{ 
                    mt: 1, 
                    backgroundColor: "#0d5eff",
                    '&:hover': {
                      backgroundColor: "#0a4cd6",
                    }
                  }}
                >
                  {filterLoading ? "Applying..." : "Apply Filters"}
                </Button>
              </>
            )}
          </Paper>
        </Grid>
        
        {/* Main content area */}
        <Grid item xs={12} md={9}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              minHeight: "70vh",
              position: "relative",
            }}
          >
            {loading ? (
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Loading case studies...
                </Typography>
              </Box>
            ) : queryLoading ? (
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Searching...
                </Typography>
              </Box>
            ) : caseStudies.length > 0 ? (
              <>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "#012063" }}>
                  {isSearchActive
                    ? `Search Results (${caseStudies.length})`
                    : filtersApplied
                      ? `Case Study Results (${caseStudies.length})`
                      : `Total Listed Case Studies (${caseStudies.length})`}
                </Typography>



                <TableContainer 
                  sx={{ 
                    maxHeight: '50vh', 
                    overflow: 'auto',
                    border: '1px solid #ddd',
                    '& .MuiTable-root': {
                      borderCollapse: 'collapse',
                      tableLayout: 'auto',
                      width: '100%'
                    }
                  }}
                >
                  <Table 
                    aria-label="case studies table" 
                    stickyHeader 
                  >
                    <TableHead>
                      <TableRow>
                        {/* Table Headers */}
                        <TableCell
                          sx={{
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            backgroundColor: "#f5f5f5",
                            padding: '4px 6px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle',
                            minWidth: 220,
                            maxWidth: 350,
                            overflow: 'hidden',
                            textAlign: 'left'
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === 'case_study_name'}
                            direction={orderBy === 'case_study_name' ? order : 'asc'}
                            onClick={() => handleRequestSort('case_study_name')}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Case Study Name</Typography>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            backgroundColor: "#f5f5f5",
                            padding: '4px 6px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle',
                            minWidth: 120,
                            maxWidth: 180,
                            overflow: 'hidden',
                            textAlign: 'left'
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === 'account_name'}
                            direction={orderBy === 'account_name' ? order : 'asc'}
                            onClick={() => handleRequestSort('account_name')}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Account</Typography>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            backgroundColor: "#f5f5f5",
                            padding: '4px 6px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle',
                            minWidth: 140,
                            maxWidth: 200,
                            overflow: 'hidden',
                            textAlign: 'left'
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === 'industry'}
                            direction={orderBy === 'industry' ? order : 'asc'}
                            onClick={() => handleRequestSort('industry')}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Industry</Typography>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            backgroundColor: "#f5f5f5",
                            padding: '4px 6px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle',
                            minWidth: 100,
                            maxWidth: 120,
                            overflow: 'hidden',
                            textAlign: 'left'
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === 'end_date'}
                            direction={orderBy === 'end_date' ? order : 'asc'}
                            onClick={() => handleRequestSort('end_date')}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>End Date</Typography>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            backgroundColor: "#f5f5f5",
                            padding: '4px 6px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle',
                            minWidth: 100,
                            maxWidth: 120,
                            overflow: 'hidden',
                            textAlign: 'left'
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === 'team_size'}
                            direction={orderBy === 'team_size' ? order : 'asc'}
                            onClick={() => handleRequestSort('team_size')}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Team Size</Typography>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            backgroundColor: "#f5f5f5",
                            padding: '4px 6px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle',
                            minWidth: 140,
                            maxWidth: 180,
                            overflow: 'hidden',
                            textAlign: 'left'
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === 'practice_unit'}
                            direction={orderBy === 'practice_unit' ? order : 'asc'}
                            onClick={() => handleRequestSort('practice_unit')}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Practice Unit</Typography>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            backgroundColor: "#f5f5f5",
                            padding: '4px 6px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle',
                            minWidth: 140,
                            maxWidth: 200,
                            overflow: 'hidden',
                            textAlign: 'left'
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === 'capability_area'}
                            direction={orderBy === 'capability_area' ? order : 'asc'}
                            onClick={() => handleRequestSort('capability_area')}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Capability Area</Typography>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            border: '1px solid #ddd',
                            fontWeight: 'bold',
                            backgroundColor: "#f5f5f5",
                            padding: '4px 6px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle',
                            minWidth: 80,
                            maxWidth: 100,
                            overflow: 'hidden',
                            textAlign: 'left'
                          }}
                        >
                          Match
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.map((study, index) => {
                        const globalIndex = page * rowsPerPage + index;
                        return (
                          <TableRow key={globalIndex} hover>
                            <TableCell
                              sx={{
                                cursor: 'pointer',
                                color: "#1976d2",
                                fontWeight: 500,
                                textDecoration: "none",
                                '&:hover': { textDecoration: "underline" }
                              }}
                              title={study.case_study_name || study.caseStudyName || study.name || ""}
                              onClick={() => handleCaseStudyClick(study)}
                            >
                              <span>
                                {study.case_study_name || study.caseStudyName || study.name || ""}
                              </span>
                            </TableCell>
                            <TableCell>
                              {study.account_name || study.account || ""}
                            </TableCell>
                            <TableCell>
                              {study.industry || ""}
                            </TableCell>
                            <TableCell>
                              {study.end_date
                                ? new Date(study.end_date).toLocaleDateString()
                                : ""}
                            </TableCell>
                            <TableCell>
                              {study.team_size || study.teamSize || ""}
                            </TableCell>
                            <TableCell>
                              {study.practice_unit || study.practiceUnit || ""}
                            </TableCell>
                            <TableCell>
                              {Array.isArray(study.capability_area)
                                ? study.capability_area.join(", ")
                                : study.capability_area || ""}
                            </TableCell>
                            <TableCell>
                              {study.match_type ? (
                                <span
                                  style={{
                                    color: study.match_type === "exact" ? "#1976d2" : "#1976d2",
                                    fontWeight: 500,
                                    fontSize: "0.95rem",
                                    fontStyle: "italic",
                                    opacity: 0.8,
                                    float: "right",
                                  }}
                                >
                                  {study.match_type === "exact"
                                    ? "Exact"
                                    : study.match_type === "relevant"
                                    ? "Relevant"
                                    : ""}
                                </span>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination controls */}
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 75, 100]}
                  component="div"
                  count={caseStudies.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Rows:"
                  sx={{
                    mt: 'auto',
                    overflow: 'visible',
                    '.MuiTablePagination-toolbar': {
                      minHeight: '48px',
                      pl: 1
                    },
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                      fontSize: '0.875rem',
                      color: 'black'
                    }
                  }}
                />
                {/* --- KEEP ONLY THIS SEARCH BAR --- */}
                <Box sx={{ width: "100%", mt: 2, display: "flex", alignItems: "center" }}>
                  <form onSubmit={handleQuerySearch} style={{ flex: 1, display: "flex" }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Search case studies..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      disabled={loading}
                      InputProps={{
                        endAdornment: (
                          <IconButton type="submit" aria-label="search" disabled={loading}>
                            <SearchIcon />
                          </IconButton>
                        ),
                      }}
                    />
                    <Button
                      variant="text"
                      sx={{ ml: 1 }}
                      onClick={handleClearSearch}
                      disabled={!isSearchActive && !searchQuery}
                      type="button"
                    >
                      CLEAR RESULT
                    </Button>
                  </form>
                  {queryError && (
                    <Typography color="error" sx={{ ml: 2 }}>
                      {queryError}
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "40vh",
                }}
              >
                {(filtersApplied || isSearchActive) ? (
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    No Similar result found
                  </Typography>
                ) : (
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    Case studies will be shown here
                  </Typography>
                )}
                {queryError && (
                  <Typography color="error" sx={{ mt: 2 }}>
                    {queryError}
                  </Typography>
                )}
                {/* Search bar for empty state */}
                <Box sx={{ width: "100%", mt: 2, display: "flex", alignItems: "center" }}>
                  <form onSubmit={handleQuerySearch} style={{ flex: 1, display: "flex" }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Search case studies..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      disabled={loading}
                      InputProps={{
                        endAdornment: (
                          <IconButton type="submit" aria-label="search" disabled={loading}>
                            <SearchIcon />
                          </IconButton>
                        ),
                      }}
                    />
                    <Button
                      variant="text"
                      sx={{ ml: 1 }}
                      onClick={handleClearSearch}
                      disabled={!isSearchActive && !searchQuery}
                      type="button"
                    >
                      CLEAR RESULT
                    </Button>
                  </form>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Sidebar Drawer for Case Study Details */}
      <Drawer
        anchor="right"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        PaperProps={{
          sx: {
            width: "50vw", // Use the full page width for the sidebar
            height: "100vh",
            p: 0,
            color: "#000",
            backgroundColor: "#fff",
          }
        }}
      >
        {/* Tabs Header */}
        <Box sx={{ borderBottom: '1px solid #ddd', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: "#000" }}>
          <Tabs
            value={sidebarTab}
            onChange={(e, newValue) => setSidebarTab(newValue)}
            textColor="inherit"
            indicatorColor="primary"
            sx={{ borderBottom: 'none', color: "#000" }}
          >
            <Tab label="Details" sx={{ minWidth: 100, color: "#000" }} />
            <Tab label="Presentation" sx={{ minWidth: 100, color: "#000" }} />
          </Tabs>
          <IconButton onClick={() => setSidebarOpen(false)} size="small" sx={{ color: "#000" }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Tab Content - Details */}
        {sidebarTab === 0 && selectedCaseStudy && (
          <Box sx={{ p: { xs: 2, md: 6 }, color: "#000", backgroundColor: "#fff", minHeight: "100vh" }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: "#000", fontSize: { xs: "1.5rem", md: "2.2rem" } }}>
              {selectedCaseStudy.case_study_name || selectedCaseStudy.caseStudyName || selectedCaseStudy.name}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={4}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#000", fontSize: { xs: "1rem", md: "1.1rem" } }}>
                  Account
                </Typography>
                <Typography variant="body2" sx={{ color: "#000", fontSize: { xs: "0.95rem", md: "1rem" } }}>
                  {selectedCaseStudy.account_name || selectedCaseStudy.account || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#000", fontSize: { xs: "1rem", md: "1.1rem" } }}>
                  Industry
                </Typography>
                <Typography variant="body2" sx={{ color: "#000", fontSize: { xs: "0.95rem", md: "1rem" } }}>
                  {selectedCaseStudy.industry || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#000", fontSize: { xs: "1rem", md: "1.1rem" } }}>
                  Team Size
                </Typography>
                <Typography variant="body2" sx={{ color: "#000", fontSize: { xs: "0.95rem", md: "1rem" } }}>
                  {selectedCaseStudy.team_size || selectedCaseStudy.teamSize ? `${selectedCaseStudy.team_size || selectedCaseStudy.teamSize} FTE` : "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#000", fontSize: { xs: "1rem", md: "1.1rem" } }}>
                  Practice Unit
                </Typography>
                <Typography variant="body2" sx={{ color: "#000", fontSize: { xs: "0.95rem", md: "1rem" } }}>
                  {selectedCaseStudy.practice_unit || selectedCaseStudy.practiceUnit || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#000", fontSize: { xs: "1rem", md: "1.1rem" } }}>
                  Capability Area
                </Typography>
                               <Typography variant="body2" sx={{ color: "#000", fontSize: { xs: "0.95rem", md: "1rem" } }}>
                  {Array.isArray(selectedCaseStudy.capability_area)
                    ? selectedCaseStudy.capability_area.join(", ")
                    : selectedCaseStudy.capability_area || "-"}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ my: 4, borderColor: '#ddd' }} />
          </Box>
        )}
        
        {/* Tab Content - Presentation */}
        {sidebarTab === 1 && selectedCaseStudy && (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', borderRadius: 2, boxShadow: 2, mb: 2 }}>
              {previewLoading && (
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255, 255, 255, 0.8)' }}>
                  <CircularProgress />
                </Box>
              )}
              {previewError && (
                <Box sx={{ p: 2, bgcolor: '#fbe9e7', borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" color="error">
                    {previewError}
                  </Typography>
                </Box>
              )}
              <iframe
                ref={pptIframeRef}
                src={pptUrl || ""}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: pptUrl ? 'block' : 'none'
                }}
                title="Presentation Preview"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleFullScreen}
                disabled={previewLoading || !pptUrl}
                sx={{
                  backgroundColor: "#0d5eff",
                  '&:hover': { backgroundColor: "#0a4cd6" },
                  borderRadius: 4,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  height: 40,
                  px: 2
                }}
              >
                <FullscreenIcon sx={{ mr: 1 }} />
                Full Screen
              </Button>
              <Button
                variant="outlined"
                onClick={handleDownloadPPT}
                disabled={previewLoading}
                sx={{
                  borderColor: "#0d5eff",
                  color: "#0d5eff",
                  '&:hover': {
                    borderColor: "#0a4cd6",
                    color: "#0a4cd6",
                  },
                  borderRadius: 4,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  height: 40,
                  px: 2
                }}
              >
                <DownloadIcon sx={{ mr: 1 }} />
                Download
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>
    </div>
  );
};

export default CaseStudy;